// src/utils/firebaseConfig.js

import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFirestore } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { ref, onValue, set, onDisconnect, serverTimestamp } from 'firebase/database';

// Firebase configuration object using environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL
};

// Validate environment variables
const validateConfig = () => {
  const requiredVars = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID',
    'REACT_APP_FIREBASE_DATABASE_URL'
  ];

  for (const varName of requiredVars) {
    if (!process.env[varName]) {
      throw new Error(`Missing required environment variable: ${varName}`);
    }
  }
};

// Validate configuration before initializing
validateConfig();

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const rtdb = getDatabase(app);

// Set persistence and additional security configurations
setPersistence(auth, browserSessionPersistence)
  .catch((error) => {
    // Use a generic error message in production
    const errorMessage = process.env.NODE_ENV === 'production'
      ? 'Authentication configuration error'
      : error.message;
    console.error("Failed to set persistence:", errorMessage);
  });

// Configure session timeout (15 minutes)
let sessionTimeout;
auth.onAuthStateChanged((user) => {
  if (user) {
    // Reset timeout whenever there's activity
    if (sessionTimeout) clearTimeout(sessionTimeout);
    sessionTimeout = setTimeout(() => {
      auth.signOut();
    }, 15 * 60 * 1000); // 15 minutes
  }
});

// Add activity listeners to reset timeout
const resetTimeout = () => {
  if (sessionTimeout) clearTimeout(sessionTimeout);
  if (auth.currentUser) {
    sessionTimeout = setTimeout(() => {
      auth.signOut();
    }, 15 * 60 * 1000);
  }
};

// Add event listeners for user activity
if (typeof window !== 'undefined') {
  ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
    window.addEventListener(event, resetTimeout);
  });
}

export const presenceTracker = {
  startTracking: async () => {
    try {
      if (!auth.currentUser) return;
      
      const uid = auth.currentUser.uid;
      const userStatusRef = ref(rtdb, `/status/${uid}`);
      
      // Create a reference to the special '.info/connected' path in
      // Realtime Database which returns `true` when connected
      const isOfflineForDatabase = {
        state: 'offline',
        last_changed: serverTimestamp(),
      };
      
      const isOnlineForDatabase = {
        state: 'online',
        last_changed: serverTimestamp(),
      };
      
      onValue(ref(rtdb, '.info/connected'), (snapshot) => {
        if (snapshot.val() === false) {
          return;
        }
        
        // If we're not currently connected, don't do anything.
        onDisconnect(userStatusRef)
          .set(isOfflineForDatabase)
          .then(() => {
            set(userStatusRef, isOnlineForDatabase);
          })
          .catch(error => {
            console.warn('Presence tracking error (can be ignored during registration):', error.message);
            // Don't throw the error as it's not critical
          });
      });
      
      return () => {
        // Cleanup function
        try {
          const userStatusRef = ref(rtdb, `/status/${uid}`);
          set(userStatusRef, isOfflineForDatabase);
        } catch (error) {
          console.warn('Error cleaning up presence:', error);
        }
      };
    } catch (error) {
      console.warn('Presence tracking error (can be ignored during registration):', error.message);
      // Return empty cleanup function
      return () => {};
    }
  }
};

export {
  auth,
  db,
  storage,
  app,
  rtdb
};